import { push } from '@lagunovsky/redux-react-router'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import HomeAction from 'home/actions/HomeAction'
import { omit } from 'lodash'
import { ANTENNA_ID } from 'materiel/constants/MaterielConstants'
import { MATERIEL_STATE_IN_STOCK_ID } from 'materiel/constants/MaterielStateConstants'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import i18n from 'simple-react-i18n'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { H_MAT_ANTENNA } from '../../../../account/constants/AccessRulesConstants'
import UpdatePanel from '../../../../components/updatePanel/UpdatePanel'
import { componentHasHabilitations } from '../../../../utils/HabilitationUtil'
import { updateMaterialize } from '../../../../utils/MaterializeUtils'
import { isSaveBlocked } from '../../../utils/MaterielUtils'
import AdministratorPanel from '../../administrator/AdministratorPanel'
import AssigmentChartPanel from '../../chart/AssigmentChartPanel'
import LastSituationMarkerPanel from '../../situation/LastSituationMarkerPanel'
import SituationPanel from '../../situation/SituationPanel'
import AntennaAction from '../actions/AntennaAction'
import AntennaPanel from './AntennaPanel'

const AntennaApp = () => {
    const [newAntenna, setNewAntenna] = useState({})
    const [isEditMode, setIsEditMode] = useState(false)

    const antenna = useSelector(store => store.AntennaReducer.antenna)
    const antennas = useSelector(store => store.AntennaReducer.antennas)
    const antennaSituations = useSelector(store => store.AntennaReducer.antennaSituations)
    const antennasLastSituations = useSelector(store => store.AntennaReducer.antennasLastSituations)
    const accountUser = useSelector(store => store.AccountReducer.accountUser)
    const materielSettingRules = useSelector(store => store.MaterielReducer.materielSettingRules)

    const dispatch = useDispatch()
    const params = useParams()

    const changeAdministrator = administrator => setNewAntenna(prev => ({ ...prev, administrator }))

    const changeStateAttribute = changes => setNewAntenna(prev => ({ ...prev, ...changes }))

    const buildActions = () => {
        if (params.id === 'new' && !antenna.id) {
            return {
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === ANTENNA_ID)
                    if (isSaveBlocked(antennas, newAntenna, rules, 'antenneType')) return

                    dispatch(AntennaAction.createAntenna(newAntenna)).then(id => {
                        if (id) {
                            dispatch(AntennaAction.saveAntennaSituation({
                                idAntenne: id,
                                situationDate: newAntenna.purchaseDate,
                                statusCode: MATERIEL_STATE_IN_STOCK_ID,
                            }))
                        }
                    })
                },
                cancel: () => {
                    dispatch(push('/materiel/antenna'))
                },
            }
        }
        if (isEditMode) {
            return {
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === ANTENNA_ID)
                    if (isSaveBlocked(antennas, newAntenna, rules, 'antenneType')) return

                    dispatch(AntennaAction.updateAntenna(newAntenna)).then(success => success && setIsEditMode(false))
                },
                cancel: () => {
                    setNewAntenna(antenna)
                    setIsEditMode(false)
                },
            }
        }
        return {
            edit: () => {
                setIsEditMode(true)
            },
            delete: () => {
                dispatch(AntennaAction.deleteAntenna(params.id))
            },
        }
    }

    useActions(() => {
        updateMaterialize()
        const actions = buildActions()
        if (accountUser.consultant === '1') {
            return omit(actions, ['save', 'edit', 'delete'])
        }
        return actions
    }, [isEditMode, newAntenna])

    useTitle(() => {
        const { name = '', serialNumber = '', code = '', id } = antenna
        const title = name || serialNumber || code || id
        return ([
            {
                title: i18n.materiels,
                href: 'materiel',
            },
            {
                title: i18n.antennas,
                href: 'materiel/antenna',
            },
            {
                title,
                href: `materiel/antenna/${params.id}`,
            },
        ])
    }, [antenna])

    useEffect(() => {
        if (!componentHasHabilitations(H_MAT_ANTENNA)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
        }
        dispatch(HomeAction.setHelpLink('materiel', ''))
        dispatch(AntennaAction.fetchAntennaTypes())
        if (!antennas.length) {
            dispatch(AntennaAction.fetchAntennas())
        }
        return () => dispatch(AntennaAction.resetAntenna())
    }, [])

    useEffect(() => {
        if (params.id !== 'new') {
            dispatch(AntennaAction.fetchAntenna(params.id)).then(json => {
                if (json && json.id) {
                    dispatch(AntennaAction.fetchAntennaSituations(json.id))
                    setNewAntenna({ ...json })
                }
            })
            setIsEditMode(false)
        } else {
            dispatch(AntennaAction.resetAntenna())
            setNewAntenna({})
            setIsEditMode(true)
        }
    }, [params.id])

    const deleteSituation = id => dispatch(AntennaAction.deleteAntennaSituation(id, params.id))

    const getAntennasFormat = () => antennas.map(item => {
        const number = item.serialNumber || item.code || ''
        return {
            id: item.id,
            label: item.name ? `${item.name} - ${number}` : number,
        }
    })

    const disabled = { active: isEditMode, disabled: !isEditMode }
    return (
        <Card>
            <CardContent>
                <Grid container size={12} spacing={2}>
                    <Grid container size={9}>
                        <LastSituationMarkerPanel materielSituation={antennaSituations} />
                        <Grid container size={'grow'}>
                            <Grid size={12}>
                                <AntennaPanel
                                    antenna={newAntenna}
                                    onChange={changeStateAttribute}
                                    {...disabled}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={3}>
                        <Stack spacing={1}>
                            <UpdatePanel
                                updateDate={antenna.updateDate}
                                updateLogin={antenna.updateLogin}
                                jobExecId={antenna.jobExecutionId}
                            />
                            <Card>
                                <CardContent>
                                    <AdministratorPanel
                                        onChange={changeAdministrator}
                                        selectedValue={newAntenna.administrator}
                                        isActive={isEditMode}
                                    />
                                </CardContent>
                            </Card>
                            <AssigmentChartPanel materielSituation={antennaSituations} />
                        </Stack>
                    </Grid>
                    <Grid size={12}>
                        <SituationPanel
                            materielSituation={antennaSituations}
                            active={isEditMode}
                            idMateriel={antenna.id}
                            serialNumber={antenna.serialNumber}
                            purchaseDate={antenna.purchaseDate}
                            idKey={'idAntenne'}
                            lastSituations={antennasLastSituations}
                            materials={getAntennasFormat()}
                            saveFunction={situation => dispatch(AntennaAction.saveAntennaSituation(situation))}
                            deleteFunction={deleteSituation}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

export default AntennaApp
